import { computed, type Ref, ref, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { EngagementRingProps } from '../EngagementRing.vue'
import type {
    CatalogueEngagementRingSignatureStoneInput,
    EngagementRingAndSignatureStoneFragment,
} from '@/graphql/api'
import { useEngagementRingPageQuery } from '@/graphql/api'
import { useTranslator } from '@/i18n'
import { useRegionPlugin } from '@/plugins/region'
import {
    createSignatureStoneFinderPredicate,
    getEngagementRingFilterInputFromProps,
    isSameRing,
    isSameStone,
    signatureStoneToInput,
} from '@/domain/product/ring/engagement'
import type { RingBuilder } from '@/composables/useRingBuilder'
import { useDeliveryDate } from '@/views/product/composables/useDeliveryDate'

import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { createNonNullableData } from '@/composables/createNonNullableData.ts'

export async function useEngagementRingData(
    props: EngagementRingProps,
    hasEngraving: Readonly<Ref<boolean>>,
    ringBuilder: RingBuilder
) {
    const {
        addRingAndStone,
        ring: rbRing,
        stone: rbStone,
        mountFragment,
        signatureStone: rbSignatureStone,
        isValid,
    } = ringBuilder

    const is3rdStep = computed(
        () =>
            rbStone.value !== null &&
            rbRing.value !== null &&
            props.slug === rbRing.value.slug &&
            props.metal === rbRing.value.primaryMetal.systemCode &&
            props.suffixSlug === rbRing.value.suffixSlug &&
            props.shape === rbRing.value.centreStoneConstraints.shape.systemCode
    )

    const currency = useCurrencyInput()
    const { regionCode } = useRegionPlugin()
    const { locale } = useTranslator()
    const page = useRoute().path

    const responsePage = useEngagementRingPageQuery({
        variables: computed(() => {
            return {
                locale: locale.value,
                currency: currency.value,
                region: regionCode.value,
                page,
                pageFallback: 'engagement-rings',
                engagementRingFilter:
                    getEngagementRingFilterInputFromProps(props),
            }
        }),
    })

    const catalogueDeliveryDate = useDeliveryDate(responsePage, (data) =>
        hasEngraving.value
            ? data.catalogueEngagementRing?.hasEngravingDeliveryDate
            : data.catalogueEngagementRing?.noEngravingDeliveryDate
    )

    const {
        fetching,
        stale,
        error,
        data: originData,
        executeQuery: executePageQuery,
    } = responsePage

    const data = createNonNullableData(originData, fetching)

    if (
        props.nonSku &&
        (!isSameRing(rbRing.value, props) ||
            !isSameStone(rbStone.value, props.nonSku))
    ) {
        await addRingAndStone(
            getEngagementRingFilterInputFromProps(props),
            props.nonSku
        )
    }

    await responsePage

    const cmsBlocks = toRef(() => data.value?.pageProductDetails?.blocks)

    const ring = toRef(() => data.value?.catalogueEngagementRing)

    const signatureStone = ref<
        CatalogueEngagementRingSignatureStoneInput | undefined
    >()

    watch(
        ring,
        (newRing) => {
            if (!newRing) {
                return
            }

            const getters: (() =>
                | EngagementRingAndSignatureStoneFragment
                | undefined
                | null)[] = []

            getters.push(() => {
                const mountStone = mountFragment.value?.signatureStone
                if (!mountStone) {
                    return
                }
                const mountStonePredicate = createSignatureStoneFinderPredicate(
                    signatureStoneToInput(mountStone)
                )
                return newRing.signatureStones.find((s) =>
                    mountStonePredicate(s.signatureStone)
                )
            })

            getters.push(() => {
                const rbStone = rbSignatureStone.value

                if (!rbStone) {
                    return
                }

                const rbStonePredicate =
                    createSignatureStoneFinderPredicate(rbStone)
                return newRing.signatureStones.find((s) =>
                    rbStonePredicate(s.signatureStone)
                )
            })

            getters.push(() => {
                const ringStone = newRing.signatureStone
                if (!ringStone || !ringStone.colors[0]) {
                    return
                }
                const ringStonePredicate = createSignatureStoneFinderPredicate({
                    color: ringStone.colors[0].systemCode,
                    crystal: ringStone.crystal.systemCode,
                    width: ringStone.width,
                })
                return newRing.signatureStones.find((s) =>
                    ringStonePredicate(s.signatureStone)
                )
            })

            getters.push(() => {
                return newRing.signatureStones.find(
                    (stone) => stone.signatureStone.isDefault
                )
            })

            getters.push(() => {
                return newRing.signatureStones[0]
            })

            let output = undefined

            for (let i = 0; i < getters.length && output === undefined; i++) {
                output = getters[i]!()
            }

            if (output) {
                signatureStone.value = signatureStoneToInput(
                    output.signatureStone
                )
            }
        },
        { immediate: true }
    )

    const signatureStoneOutput = computed(() => {
        const stones = ring.value?.signatureStones

        if (!stones || !signatureStone.value) {
            return
        }
        const predicate = createSignatureStoneFinderPredicate(
            signatureStone.value
        )
        return stones.find((value) => predicate(value.signatureStone))
    })

    const signatureStoneLabel = computed(() => {
        const sigStoneOutput = signatureStoneOutput.value
        if (!sigStoneOutput) {
            return undefined
        }

        if (sigStoneOutput.signatureStone.crystal.systemCode === 'DIAMOND') {
            return sigStoneOutput.signatureStone.crystal.translation.name
        }

        return `${sigStoneOutput.signatureStone.color.translation.name} ${
            sigStoneOutput.signatureStone.crystal.translation.name
        }`
    })

    return {
        ring,
        fetching,
        stale,
        error,
        responsePage,
        cmsBlocks,
        executePageQuery,
        is3rdStep,
        isValidCreation: isValid,
        deliveryDate: catalogueDeliveryDate,
        signatureStone,
        signatureStoneLabel,
        signatureStoneOutput,
    }
}
